<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div :class="[inputCssClass, errorMessages ? 'field-error' : '']" class="rounded-lg">
        <Dialog
            v-if="showDialog"
            :value="showDialog"
            :header-text="$t('Add new client')"
            :width="360"
            header_classes="black white--text"
            persistent
            @close_modal="close_modal"
        >
            <v-container
                v-if="form_data"
                class="px-0"
            >
                <v-select
                    v-if="accounts.length > 1"
                    v-model="form_data.account_id"
                    :label="$t('Account')"
                    :items="accounts"
                    item-text="name"
                    item-value="id"
                    outlined
                    :error-messages="form_data.account_id_error"
                ></v-select>
                <v-text-field
                    v-model="form_data.name"
                    :label="$t('Client name')"
                    outlined
                    :error-messages="form_data.name_error"
                ></v-text-field>
            </v-container>
            <template slot="footer">
                <v-spacer />
                <v-btn
                    text
                    @click="close_modal"
                >
                    {{ $t("Close") }}
                </v-btn>
                <v-btn
                    class="primary inverted--text"
                    :loading="loading"
                    @click="save_client"
                >
                    {{ $t("Add") }}
                </v-btn>
            </template>
        </Dialog>
        <div class="global-label">{{ label }}</div>
        <div
            class="client-select-button"
            :class="menu_open ? 'menu-open' : ''"
        >
            <div v-if="disabled" tabindex="0" class="disp">
                <div v-if="client_id && client" class="d-flex">
                    <router-link
                        :to="{ name: 'client_profile', params: { id: client.id }}"
                        style="text-decoration: none"
                        @click.native="$emit('close_click')"
                    >
                        <ClientAvatar
                            :color="client.color"
                            :initials="client.client_initials"
                            :size="avatarSize"
                        ></ClientAvatar>
                    </router-link>
                    <div class="val ml-2 pr-1">{{ client.name }}</div>
                </div>
                <div v-else>
                    <div class="val ml-2" style="color: var(--v-n4-base);"></div>
                </div>
            </div>
            <DropMenu
                v-else
                :items="items"
                :value="items.filter(x => x.id === client_id)"
                name-key="name"
                value-key="id"
                searchable
                deselect
                :max-width="300"
                :menu-state.sync="menu_open"
                :show-actions-slot="newClientEnabled"
                @input="toggle_selection($event)"
            >
                <template #default="{ on }">
                    <div tabindex="0" class="disp cursor-pointer" test-id="task-client-select-button" v-on="on">
                        <div v-if="client_id && client" class="d-flex">
                            <router-link
                                :to="{ name: 'client_profile', params: { id: client.id }}"
                                style="text-decoration: none"
                                @click.native="$emit('close_click')"
                            >
                                <ClientAvatar
                                    :color="client.color"
                                    :initials="client.client_initials"
                                    :size="avatarSize"
                                    @click="redirectToClient(client.id)"
                                ></ClientAvatar>
                            </router-link>
                            <div class="val ml-2 pr-1 font-weight-medium">{{ client.name }}</div>
                        </div>
                        <div v-else>
                            <div class="val ml-2" style="color: var(--v-n4-base);">
                                <template v-if="showPlaceholder">
                                    {{ emptyText ? emptyText : $t("Select client") }}
                                </template>
                            </div>

                            <div :class="['arrow', menu_open ? 'arrow--open' : '']"><icon size="20" icon="ChevronDown" /></div>
                        </div>
                    </div>
                </template>
                <template #item-avatar="{ item }">
                    <v-list-item-avatar
                        min-width="24"
                        width="24"
                        tile
                        class="mr-2"
                    >
                        <ClientAvatar
                            :size="24"
                            :color="item.color"
                            :initials="item.client_initials"
                        ></ClientAvatar>
                    </v-list-item-avatar>
                </template>
                <template #actions="{ on }" v-if="newClientEnabled">
                    <v-btn
                        text
                        small
                        color="primary"
                        @click="new_client"
                    >
                        <icon
                            size="18"
                            icon="Plus"
                        />
                        <span class="ml-3">{{ $t("New client") }}</span>
                    </v-btn>
                </template>

            </DropMenu>
            <div :class="['details', errorMessages ? 'error--text' : '']">
                {{ errorMessages }}
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { ref, computed, watch } from "@vue/composition-api";
import ClientAvatar from "../components/ClientAvatar.vue";
import ClientAvatarListItem from "../components/ClientAvatarListItem.vue";
import Dialog from "../components/Dialog.vue";
import ClientModule from "../store/modules/client";
import AppModule from "../store/modules/app";
import AccountModule from "@/store/modules/account";
import { $t } from "@/i18n";
import {FEATURE_ADD_TASK_CLIENT, isFeatureAllowed} from "@/modules/subscription/allowed_features";
import DropMenu from "@/components/ModernFilterBar/components/DropMenu.vue";

export default {
    name: "ClientsField",
    components: {
        DropMenu,
        ClientAvatar,
        ClientAvatarListItem,
        Dialog
    },
    props: {
        label: {
            type: String,
            default: null
        },
        value: {
            type: Number,
            required: true
        },
        items: {
            type: Array,
            required: true
        },
        errorMessages: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        autofocusField: {
            type: String,
            default: ''
        },
        inputCssClass: {
            type: String,
            default: "task-field"
        },
        avatarSize: {
            type: Number,
            default: 32
        },
        emptyText: {
            type: String,
            default: null
        },
        showPlaceholder: {
            type: Boolean,
            default: true
        },
        newClientEnabled: {
            type: Boolean,
            default: false
        }
    },
    setup(props, ctx) {
        const { saveClient } = ClientModule();
        const { handleErrorResponse, clearValidationErrors, appendClassifiersCache, rights_masks } = AppModule();
        const { current_account, accounts_info, available_account_for_clients } = AccountModule();
        const menu_open = ref(false);
        const client_id = ref(props.value != null ? props.value : 0);
        const showDialog = ref(false);
        const form_data = ref(null);
        const loading = ref(false);

        const client = computed(function() {
            if (client_id.value === 0) return null;
            let ret = props.items.filter(x => x.id === client_id.value);
            return ret.length === 1
                ? ret[0]
                : { name: "#{0}".format(client_id.value), client_initials: "#", color: "color-35" };
        });

        const accounts = computed(function() {
            return accounts_info.value
                .filter(function(x) {
                    return (x.rights & rights_masks.value.manage_client) === rights_masks.value.manage_client;
                })
                .filter(account => {
                    return isFeatureAllowed(FEATURE_ADD_TASK_CLIENT, account)
                })
                .filter(account => {
                    return available_account_for_clients.value.map(a => a.id).includes(account.id)
                })
                .map(function(x) {
                    return { id: x.id, name: "{0} {1}".format(x.name, x.surname).trim() };
                });
        });

        const watchValue = computed(function() {
            return props.value;
        });

        const toggle_selection = (items) => {
            if (items.length) {
                if (items[0].id === client_id.value) {
                    client_id.value = 0
                } else {
                    client_id.value = items[0].id
                }
            } else {
                client_id.value = 0;
            }
        };

        const new_client = function() {
            if (accounts.value.length === 0) return;
            form_data.value = Vue.prepare_form_data(
                { name: "", account_id: accounts.value[0].id },
                { name: "text", account_id: "integer" }
            );
            menu_open.value = false;
            showDialog.value = true;
        };
        const save_client = function() {
            let accounts = accounts_info.value.filter(x => x.id === form_data.value.account_id);
            if (accounts.length === 0) {
                form_data.value.account_id_error = $t("Required field");
                return;
            }

            loading.value = true;
            clearValidationErrors(form_data.value);

            let initials = form_data.value.name
                .trim()
                .substr(0, 1)
                .toUpperCase();
            if (!initials) initials = "-";

            let data = {
                type: "client",
                account_id: form_data.value.account_id,
                name: form_data.value.name.trim(),
                reg_code: "",
                vat_code: "",
                client_initials: initials,
                color: "color-{0}".format(Math.floor(Math.random() * 48 + 1)),
                default_person_id: current_account.value.id,
                address_street: null,
                address_city: null,
                address_state: null,
                address_zip: null,
                address_country_code: accounts[0].address_country_code,
                auto_add_roles: true,
                contacts: [],
                locale_code: accounts[0].locale_code,
                status: 'active',
                members: [{ person_id: current_account.value.id, granted_role: "admin" }]
            };

            saveClient(data)
                .then(function(resp) {
                    appendClassifiersCache("clients", resp, form_data.value.account_id);
                    loading.value = false;
                    showDialog.value = false;
                    client_id.value = resp.id;
                    ctx.emit("change", client_id.value);
                })
                .catch(function(ex) {
                    loading.value = false;
                    handleErrorResponse(form_data.value, ex);
                });
        };

        const close_modal = function() {
            showDialog.value = false;
        };

        watch(menu_open, function(val, prev_val) {
            if (prev_val === undefined) return;

            if (props.value !== client_id.value) {
                ctx.emit("change", client_id.value);
            }
        });
        watch(watchValue, (val) => {
            client_id.value = val != null ? val : 0;
        });

        const watchAutoFocusField = computed(function() {
            return props.autofocusField;
        });

        watch(watchAutoFocusField, function () {
            if (props.autofocusField === 'clients') {
                setTimeout(() => menu_open.value = true, 500)
            }
        })

        return {
            client_id,
            client,
            menu_open,
            showDialog,
            form_data,
            loading,
            accounts,
            blur,
            toggle_selection,
            new_client,
            save_client,
            close_modal
        };
    }
};
</script>

<style scoped lang="scss">
.client-select-button {
    &.menu-open {
        .arrow {
            .v-icon {
                transform: rotate(180deg);
            }

        }
    }
}
</style>